import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, Lock, Loader2, MapPin, Store, Pencil, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { PlatePreview } from "@/components/PlatePreview";
import { LockerPicker } from "@/components/LockerPicker";
import { api, cartTotals, errorMessage, loadCheckout, priceFor, saveCheckout } from "@/lib/api";
import { useI18n } from "@/i18n";

const COLOR_HEX = { white: "#FFFFFF", blue: "#2563EB", pink: "#EC4899", red: "#EF4444" };

const StepTitle = ({ n, title, hint }) => (
  <div className="mb-4 flex items-start gap-3">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black font-mono-rpm text-xs font-medium text-white">{n}</span>
    <div>
      <h3 className="font-display text-lg font-semibold leading-7">{title}</h3>
      {hint && <p className="text-sm text-zinc-600">{hint}</p>}
    </div>
  </div>
);

const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} className="mt-1.5 text-xs font-medium text-red-600" data-testid={id}>
      {children}
    </p>
  ) : null;

const Stepper = ({ value, onChange, testId, t }) => (
  <div className="inline-flex items-center rounded-xl border-[1.5px] border-zinc-200 bg-white" data-testid={testId}>
    <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="flex h-12 w-12 items-center justify-center rounded-l-xl hover:bg-zinc-100" aria-label={t("config.less")} data-testid={`${testId}-minus`}>
      <Minus className="h-4 w-4" />
    </button>
    <span className="w-10 text-center font-display text-lg font-semibold" data-testid={`${testId}-value`}>
      {value}
    </span>
    <button type="button" onClick={() => onChange(Math.min(20, value + 1))} className="flex h-12 w-12 items-center justify-center rounded-r-xl hover:bg-zinc-100" aria-label={t("config.more")} data-testid={`${testId}-plus`}>
      <Plus className="h-4 w-4" />
    </button>
  </div>
);

export const Configurator = ({ catalog, productId, setProductId, cart, setCart }) => {
  const { t, money, lang } = useI18n();
  const products = catalog?.products || [];
  const colors = catalog?.text_colors || [];
  const deliveries = catalog?.delivery || [];
  const maxLen = catalog?.max_text_len || 28;

  const saved = useMemo(loadCheckout, []);
  const [bottom, setBottom] = useState("");
  const [color, setColor] = useState("white");
  const [qty, setQty] = useState(2);
  const [itemError, setItemError] = useState("");

  const [name, setName] = useState(saved.name || "");
  const [phone, setPhone] = useState(saved.phone || "");
  const [telegram, setTelegram] = useState(saved.telegram || "");
  const [delivery, setDelivery] = useState(saved.delivery || "omniva");
  const [locker, setLocker] = useState(saved.locker || null);
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);
  const [payUrl, setPayUrl] = useState(null);

  useEffect(() => {
    saveCheckout({ name, phone, telegram, delivery, locker });
  }, [name, phone, telegram, delivery, locker]);

  const product = products.find((p) => p.id === productId) || products[0];
  const editingRef = useRef(false);

  // sensible default quantity per model: 2 frames (front + rear), 1 pack of invisible holders
  useEffect(() => {
    if (editingRef.current) {
      editingRef.current = false;
      return;
    }
    if (product) setQty(product.customizable ? 2 : 1);
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const colorHex = COLOR_HEX[color] || "#FFFFFF";
  const deliveryObj = deliveries.find((d) => d.id === delivery);

  const { gross, subtotal, discount } = useMemo(() => cartTotals(cart, products), [cart, products]);
  const total = subtotal + (cart.length ? deliveryObj?.price || 0 : 0);

  const addToCart = () => {
    if (!product) return;
    if (product.customizable && !bottom.trim()) {
      setItemError(t("config.textError"));
      return;
    }
    if (cart.length >= 10) {
      toast.error(t("config.maxItems"));
      return;
    }
    setItemError("");
    const item = {
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      product_id: product.id,
      name: product.name,
      unit_price: product.price,
      quantity: qty,
      ...(product.customizable ? { bottom_text: bottom.trim(), text_color: color } : {}),
    };
    setCart([...cart, item]);
    toast.success(t("config.added"), { description: `${product.name} × ${qty}` });
  };

  const removeItem = (key) => setCart(cart.filter((i) => i.key !== key));
  const updateQty = (key, q) => setCart(cart.map((i) => (i.key === key ? { ...i, quantity: q } : i)));
  const editItem = (it) => {
    if (it.product_id !== product?.id) editingRef.current = true;
    setProductId(it.product_id);
    if (it.bottom_text !== undefined) {
      setBottom(it.bottom_text || "");
      setColor(it.text_color || "white");
    }
    setQty(it.quantity);
    removeItem(it.key);
    document.getElementById("configurator-start")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validate = () => {
    const e = {};
    if (!cart.length) e.cart = t("checkout.errCart");
    if (deliveryObj?.requires_locker && !locker) e.locker = t("checkout.errLocker");
    if (name.trim().length < 2) e.name = t("checkout.errName");
    if (phone.replace(/[^\d]/g, "").length < 6) e.phone = t("checkout.errPhone");
    setErrors(e);
    return e;
  };

  const pay = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      toast.error(e.cart || t("checkout.errFill"));
      const first = e.cart ? "configurator-start" : `checkout-${Object.keys(e)[0]}-input`;
      (document.querySelector(`[data-testid="${first}"]`) || document.getElementById(first))?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setPaying(true);
    setPayUrl(null);
    // Stripe card fields do not render inside a frame (e.g. a preview window) -> open payment in a new tab
    let framed = false;
    try {
      framed = window.self !== window.top;
    } catch {
      framed = true;
    }
    const popup = framed ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          bottom_text: it.bottom_text,
          text_color: it.text_color,
        })),
        customer: { name: name.trim(), phone: phone.trim(), telegram: telegram.trim() || null },
        delivery: { method: delivery, locker_id: deliveryObj?.requires_locker ? locker?.id : null },
        comment: comment.trim() || null,
        origin_url: window.location.origin,
        lang,
      });
      if (!framed) {
        window.location.href = data.checkout_url;
        return;
      }
      if (popup && !popup.closed) {
        popup.location.href = data.checkout_url;
      }
      setPayUrl(data.checkout_url); // backup link in case the new tab was blocked
      setPaying(false);
    } catch (err) {
      if (popup && !popup.closed) popup.close();
      toast.error(errorMessage(err, t("genericError"), t("fieldsError")));
      setPaying(false);
    }
  };

  if (!product) return null;
  const isInvisible = !product.customizable;

  return (
    <section id="order" className="rpm-section border-t border-zinc-200 bg-zinc-50 py-16 sm:py-20 lg:py-24" data-testid="configurator-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div id="configurator-start" className="rpm-section" data-testid="configurator-start" />
        <div className="rpm-label mb-3">{t("config.label")}</div>
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("config.title")}</h2>
        <p className="mt-3 max-w-xl text-sm text-zinc-600 sm:text-base">{t("config.sub")}</p>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.05fr_1fr]">
          {/* Preview */}
          <div className="sticky top-[68px] z-20 -mx-4 bg-zinc-50 px-4 pb-2 pt-2 sm:static sm:mx-0 sm:bg-transparent sm:p-0 lg:sticky lg:top-24 lg:self-start">
            <div className="rpm-card overflow-hidden shadow-[0_10px_24px_rgba(0,0,0,0.06)] sm:shadow-none">
              <div className="hidden items-center justify-between border-b border-zinc-200 px-5 py-3 sm:flex">
                <span className="rpm-label">{t("config.preview")}</span>
                <span className="flex items-center gap-1.5 font-mono-rpm text-[11px] text-zinc-500">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--rpm-lime)] ring-1 ring-black/20" /> 520 × 110 mm
                </span>
              </div>
              <div className="rpm-grid-bg flex items-center justify-center p-3 sm:min-h-[300px] sm:p-10">
                <AnimatePresence mode="wait">
                  <motion.div key={product.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="w-full">
                    <PlatePreview
                      type={isInvisible ? "invisible" : "individual"}
                      top=""
                      bottom={bottom}
                      color={colorHex}
                      hiddenLabel={t("config.holderHidden")}
                      className="w-full"
                      testId="config-live-preview"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="flex items-center justify-between border-t border-zinc-200 px-4 py-2 sm:px-5 sm:py-4">
                <div>
                  <div className="font-display text-sm font-semibold sm:text-base">{product.name}</div>
                  <div className="text-xs text-zinc-600">
                    {qty} {isInvisible ? t("config.packs") : t("config.pcs")}
                    {product.pair_price && qty >= 2 ? ` · ${t("checkout.discount").toLowerCase()} −${money(product.price * qty - priceFor(product, qty))}` : ` × ${money(product.price)}`}
                    {!isInvisible ? ` · ${t("config.textColor")}: ${t(`colors.${color}`).toLowerCase()}` : ""}
                  </div>
                </div>
                <div className="font-display text-lg font-bold sm:text-2xl" data-testid="config-item-price">
                  {money(priceFor(product, qty))}
                </div>
              </div>
            </div>
            <p className="mt-3 hidden px-1 text-xs text-zinc-500 sm:block">{t("config.previewNote")}</p>
          </div>

          {/* Controls */}
          <div className="rpm-card p-5 sm:p-7">
            <StepTitle n="1" title={t("config.step1")} />
            <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t("config.step1")}>
              {products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={p.id === product.id}
                  data-active={p.id === product.id}
                  onClick={() => setProductId(p.id)}
                  className="rpm-tile flex items-center gap-3 p-3"
                  data-testid={`config-product-${p.id}-option`}
                >
                  {p.image && <img src={p.image} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" style={{ objectPosition: "center 55%" }} />}
                  <div className="min-w-0">
                    <div className="font-display text-sm font-semibold leading-tight">{p.name}</div>
                    <div className="mt-0.5 text-xs text-zinc-600">{t(`products.items.${p.id}.local`)}</div>
                    <div className="mt-1 font-display text-lg font-bold">
                      {money(p.price)}
                      {p.unit === "pack" ? <span className="ml-1.5 text-xs font-medium text-zinc-500">/ {t("products.packNote")}</span> : null}
                    </div>
                    {p.pair_price ? (
                      <div className="text-xs font-semibold text-[var(--rpm-orange)]">{t("products.pairDeal", { price: money(p.pair_price) })}</div>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>

            <AnimatePresence initial={false}>
              {!isInvisible && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                  <div className="rpm-dashline my-6" />
                  <StepTitle n="2" title={t("config.step2")} hint={t("config.step2hint")} />
                  <div className="space-y-4">
                    <div>
                      <div className="mb-1.5 flex justify-between">
                        <label htmlFor="bottom-text" className="text-sm font-medium">{t("config.bottom")}</label>
                        <span className="font-mono-rpm text-xs text-zinc-500">{bottom.length}/{maxLen}</span>
                      </div>
                      <input
                        id="bottom-text"
                        value={bottom}
                        maxLength={maxLen}
                        onChange={(e) => {
                          setBottom(e.target.value);
                          setItemError("");
                        }}
                        placeholder={t("config.bottomPh")}
                        className="rpm-input w-full px-4"
                        aria-invalid={!!itemError}
                        data-testid="config-bottom-text-input"
                      />
                    </div>
                    <FieldError id="config-text-error">{itemError}</FieldError>
                    <div data-testid="config-ideas">
                      <div className="mb-2 text-xs font-medium text-zinc-600">{t("config.ideas")}</div>
                      <div className="flex flex-wrap gap-2">
                        {t("config.ideaList").map((idea, i) => {
                          const active = bottom.trim() === idea;
                          return (
                            <button
                              key={idea}
                              type="button"
                              onClick={() => {
                                setBottom(idea.slice(0, maxLen));
                                setItemError("");
                              }}
                              aria-pressed={active}
                              className={`h-9 rounded-full border-[1.5px] px-3.5 font-mono-rpm text-xs font-medium transition-colors duration-200 active:scale-[0.97] ${
                                active ? "border-black bg-black text-white" : "border-zinc-200 bg-white text-black hover:border-black"
                              }`}
                              data-testid={`config-idea-${i}`}
                            >
                              {idea}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="rpm-dashline my-6" />
                  <StepTitle n="3" title={t("config.step3")} />
                  <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={t("config.step3")}>
                    {colors.map((c) => (
                      <button key={c.id} type="button" role="radio" aria-checked={color === c.id} onClick={() => setColor(c.id)} className="flex flex-col items-center gap-1.5" data-testid={`config-text-color-${c.id}`}>
                        <span className="rpm-swatch" data-active={color === c.id} style={{ background: c.hex }} />
                        <span className={`text-xs ${color === c.id ? "font-semibold text-black" : "text-zinc-600"}`}>{t(`colors.${c.id}`)}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="rpm-dashline my-6" />
            <StepTitle n={isInvisible ? "2" : "4"} title={isInvisible ? t("config.step4Pack") : t("config.step4")} hint={isInvisible ? t("config.step4PackHint") : t("config.step4hint")} />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Stepper value={qty} onChange={setQty} testId="config-qty" t={t} />
              <button type="button" onClick={addToCart} className="rpm-btn rpm-btn-lime h-12 flex-1 sm:max-w-[300px]" data-testid="config-add-to-cart-button">
                <ShoppingBag className="h-4 w-4 shrink-0" /> {t("config.add")} · {money(priceFor(product, qty))}
              </button>
            </div>
          </div>
        </div>

        {/* Checkout */}
        <div id="checkout" className="rpm-section mt-6 grid gap-6 lg:grid-cols-[1.05fr_1fr]">
          <div className="rpm-card order-2 p-5 sm:p-7 lg:order-1">
            <StepTitle n={isInvisible ? "3" : "5"} title={t("checkout.delivery")} />
            <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t("checkout.delivery")}>
              {deliveries.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={delivery === d.id}
                  data-active={delivery === d.id}
                  onClick={() => setDelivery(d.id)}
                  className="rpm-tile flex gap-3 p-4"
                  data-testid={`delivery-${d.id}-option`}
                >
                  {d.id === "omniva" ? <MapPin className="mt-0.5 h-5 w-5 shrink-0" /> : <Store className="mt-0.5 h-5 w-5 shrink-0" />}
                  <div>
                    <div className="font-display font-semibold">{t(`delivery.${d.id}.label`)}</div>
                    <div className="text-xs text-zinc-600">{t(`delivery.${d.id}.description`)}</div>
                    <div className="mt-1.5 font-display font-bold">{d.price ? money(d.price) : t("checkout.free")}</div>
                  </div>
                </button>
              ))}
            </div>
            {deliveryObj?.requires_locker && (
              <div className="mt-4">
                <div className="mb-1.5 block text-sm font-medium">{t("checkout.locker")} *</div>
                <LockerPicker
                  value={locker}
                  onChange={(l) => {
                    setLocker(l);
                    setErrors((e) => ({ ...e, locker: undefined }));
                  }}
                  invalid={!!errors.locker}
                />
                <FieldError id="checkout-locker-error">{errors.locker}</FieldError>
              </div>
            )}

            <div className="rpm-dashline my-6" />
            <StepTitle n={isInvisible ? "4" : "6"} title={t("checkout.contacts")} hint={t("checkout.contactsHint")} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium">{t("checkout.name")} *</label>
                <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("checkout.namePh")} className="rpm-input w-full px-4" aria-invalid={!!errors.name} autoComplete="name" data-testid="checkout-name-input" />
                <FieldError id="checkout-name-error">{errors.name}</FieldError>
              </div>
              <div>
                <label htmlFor="phone" className="mb-1.5 block text-sm font-medium">{t("checkout.phone")} *</label>
                <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+371 2000 0000" className="rpm-input w-full px-4" aria-invalid={!!errors.phone} autoComplete="tel" data-testid="checkout-phone-input" />
                <FieldError id="checkout-phone-error">{errors.phone}</FieldError>
              </div>
              <div>
                <label htmlFor="telegram" className="mb-1.5 block text-sm font-medium">Telegram</label>
                <input id="telegram" value={telegram} onChange={(e) => setTelegram(e.target.value)} placeholder="@username" className="rpm-input w-full px-4" data-testid="checkout-telegram-input" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="comment" className="mb-1.5 block text-sm font-medium">{t("checkout.comment")}</label>
                <textarea id="comment" value={comment} maxLength={500} onChange={(e) => setComment(e.target.value)} placeholder={t("checkout.commentPh")} rows={3} className="rpm-input !h-auto w-full px-4 py-3" data-testid="checkout-comment-input" />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-24 lg:self-start">
            <div className="rpm-card overflow-hidden" data-testid="order-summary">
              <div className="flex items-center justify-between bg-black px-5 py-4 text-white">
                <span className="font-display text-lg font-semibold">{t("checkout.yourOrder")}</span>
                <span className="font-mono-rpm text-xs text-zinc-300">
                  {cart.length} {t("checkout.pos")}
                </span>
              </div>
              <div className="p-5">
                {cart.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-zinc-300 p-5 text-center" data-testid="order-summary-empty">
                    <ShoppingBag className="mx-auto mb-2 h-6 w-6 text-zinc-400" />
                    <p className="text-sm font-medium">{t("checkout.empty")}</p>
                    <p className="mt-1 text-xs text-zinc-600">{t("checkout.emptyHint")}</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    <AnimatePresence initial={false}>
                      {cart.map((it, idx) => (
                        <motion.li key={it.key} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 20 }} className="rounded-xl border border-zinc-200 p-3" data-testid={`order-summary-item-${idx}`}>
                          <div className="flex gap-3">
                            <div className="w-24 shrink-0 sm:w-28">
                              <PlatePreview type={it.product_id === "invisible" ? "invisible" : "individual"} top="" bottom={it.bottom_text} color={COLOR_HEX[it.text_color]} hiddenLabel={t("config.holderHidden")} className="w-full" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-display text-sm font-semibold leading-tight">{it.name}</div>
                                <div className="font-display text-sm font-bold">{money(it.unit_price * it.quantity)}</div>
                              </div>
                              {it.product_id === "invisible" && <div className="mt-1 text-xs text-zinc-600">{t("products.packNote")}</div>}
                              {it.bottom_text !== undefined && (
                                <div className="mt-1 space-y-0.5 text-xs text-zinc-600">
                                  {it.bottom_text && (
                                    <div className="truncate">
                                      {t("checkout.bottomShort")}: <span className="font-mono-rpm text-black">{it.bottom_text}</span>
                                    </div>
                                  )}
                                  <div>
                                    {t("checkout.color")}: {t(`colors.${it.text_color}`)}
                                  </div>
                                </div>
                              )}
                              <div className="mt-2 flex items-center justify-between">
                                <div className="inline-flex items-center rounded-lg border border-zinc-200">
                                  <button type="button" className="flex h-8 w-8 items-center justify-center hover:bg-zinc-100" onClick={() => updateQty(it.key, Math.max(1, it.quantity - 1))} aria-label={t("config.less")} data-testid={`order-item-${idx}-minus`}>
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="w-7 text-center text-sm font-semibold" data-testid={`order-item-${idx}-qty`}>
                                    {it.quantity}
                                  </span>
                                  <button type="button" className="flex h-8 w-8 items-center justify-center hover:bg-zinc-100" onClick={() => updateQty(it.key, Math.min(20, it.quantity + 1))} aria-label={t("config.more")} data-testid={`order-item-${idx}-plus`}>
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="flex gap-1">
                                  <button type="button" onClick={() => editItem(it)} className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-zinc-100" aria-label={t("checkout.edit")} data-testid={`order-item-${idx}-edit`}>
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button type="button" onClick={() => removeItem(it.key)} className="flex h-8 w-8 items-center justify-center rounded-lg text-red-600 hover:bg-red-50" aria-label={t("checkout.remove")} data-testid={`order-item-${idx}-remove`}>
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  </ul>
                )}

                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-600">{t("checkout.goods")}</span>
                    <span className="font-medium" data-testid="order-summary-subtotal">{money(gross)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between font-medium text-[var(--rpm-orange)]" data-testid="order-summary-discount">
                      <span>{t("checkout.discount")}</span>
                      <span>−{money(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-3">
                    <span className="text-zinc-600">
                      {t("checkout.delivery")} · {t(`delivery.${delivery}.label`)}
                    </span>
                    <span className="shrink-0 font-medium" data-testid="order-summary-delivery">
                      {cart.length ? (deliveryObj?.price ? money(deliveryObj.price) : t("checkout.free")) : "—"}
                    </span>
                  </div>
                  {deliveryObj?.requires_locker && locker && (
                    <div className="truncate text-xs text-zinc-500" data-testid="order-summary-locker">
                      📍 {locker.name}
                    </div>
                  )}
                  <div className="rpm-dashline !my-3" />
                  <div className="flex items-end justify-between">
                    <span className="font-display text-base font-semibold">{t("checkout.total")}</span>
                    <span className="font-display text-3xl font-bold" data-testid="order-summary-total">{money(total)}</span>
                  </div>
                  <div className="text-right text-[11px] text-zinc-500">{t("checkout.vat")}</div>
                </div>

                <button type="button" onClick={pay} disabled={paying} className="rpm-btn rpm-btn-orange mt-5 h-14 w-full text-base" data-testid="checkout-pay-button">
                  {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}
                  {paying ? t("checkout.paying") : `${t("checkout.pay")} · ${money(total)}`}
                </button>
                {payUrl && (
                  <a
                    href={payUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rpm-btn rpm-btn-black mt-3 h-12 w-full text-sm"
                    data-testid="checkout-open-payment-link"
                  >
                    <ExternalLink className="h-4 w-4" /> {t("checkout.openPay")}
                  </a>
                )}
                {errors.cart && <FieldError id="checkout-cart-error">{errors.cart}</FieldError>}
                <p className="mt-3 text-center text-xs text-zinc-500">{t("checkout.secure")}</p>
                {catalog?.payment_mode === "test" && (
                  <p className="mt-2 rounded-lg bg-[var(--rpm-yellow)] px-3 py-2 text-center text-xs font-medium text-black" data-testid="checkout-test-mode-note">
                    {t("checkout.testMode")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
